import React, { useState } from "react"
import Link from "next/link"
import Tooltip from "@common/tooltip"
import Svg from "@common/svg"
import { isFunction, isNullOrEmpty, joinClassNames } from "@common/lib/util"
import PopoverMenuStyled from "@common/v2/dropdowns/popoverMenu"
import ActionMenu, { ActionMenuProps } from "@common/dropdowns/actionMenu"
import useIsMobile from "@common/hooks/useIsMobile"

export type MenuItem = {
  key: string
  name: string
  icon?: string
  items?: MenuItem[]
  subItemsType?: "inline" | "popover"
  url?: string
  render?: () => React.ReactNode
  action?: boolean
  openInNewTab?: boolean
}

type SideNavItemProps = MenuItem & {
  itemKey: string
  isCollapsed: boolean
  isPopoverItem?: boolean
  selectedKey?: string
  onItemClick?: (item: Partial<MenuItem>) => void
  depth?: number
}

type SideNavLinkProps = {
  itemKey: string
  name: string
  url: string
  icon: string
  render?: () => React.ReactNode
  isSelected?: boolean
  isCollapsed?: boolean
  isPopoverItem?: boolean
  action?: boolean
  onClick?: () => void
  actionMenu?: ActionMenuProps
  hasItems?: boolean
  isOpen?: boolean
  openInNewTab?: boolean
}

type SideNavProps = {
  config: MenuItem[]
  isCollapsed?: boolean
  selectedKey?: string
  onItemClick?: (item: Partial<MenuItem>) => void
}

export function getSideNavItemClasses(
  isSelected: boolean,
  isCollapsed: boolean,
  isPopoverItem: boolean,
  className?: string
) {
  const anchorClasses = joinClassNames(
    "w-full text-sm font-medium rounded-md hover:bg-accent block text-start transition ease-in-out duration-150",
    isPopoverItem ? "" : "px-2 py-1.5",
    isSelected
      ? "bg-accent text-accent-foreground font-semibold"
      : "text-accent-foreground hover:bg-accent",
    className
  )
  return anchorClasses
}

export function SideNavLink({
  itemKey,
  name,
  url,
  icon,
  render,
  isCollapsed = false,
  isPopoverItem,
  isSelected,
  action,
  onClick,
  actionMenu,
  hasItems,
  isOpen,
  openInNewTab,
}: SideNavLinkProps) {
  const anchorClasses = getSideNavItemClasses(isSelected, isCollapsed, isPopoverItem)

  const showIcon = !!icon && (!isFunction(render) || isCollapsed)
  const iconClasses = isCollapsed ? "size-6 m-2" : "size-5"

  const renderContent = () => (
    <>
      <div className="flex items-center w-full justify-between">
        <div className="flex gap-2">
          {showIcon && <Svg classes={iconClasses} name={icon} />}
          {!isCollapsed ? name : undefined}
        </div>
        <div>
          {!isCollapsed && hasItems && (
            <Svg
              name="arrow-right-short"
              classes={joinClassNames(
                "w-4 h-4 transition-transform ml-2",
                isOpen ? "rotate-90" : ""
              )}
            />
          )}
          {!isNullOrEmpty(actionMenu?.actions) && (
            <div className="opacity-0 group-hover:opacity-100 ml-auto">
              <ActionMenu {...actionMenu} />
            </div>
          )}
        </div>
      </div>
      {!isCollapsed && render?.()}
    </>
  )
  if (action || !url) {
    return (
      <button className={anchorClasses} key={itemKey} onClick={onClick}>
        {renderContent()}
      </button>
    )
  }
  return (
    <Link
      href={url}
      className={anchorClasses}
      key={itemKey}
      target={openInNewTab && "_blank"}
    >
      {renderContent()}
    </Link>
  )
}

function SideNavItem({
  itemKey,
  name,
  icon,
  action,
  items = [],
  isCollapsed,
  isPopoverItem,
  subItemsType = "inline",
  url,
  render,
  selectedKey,
  onItemClick,
  depth = 0,
  openInNewTab,
}: SideNavItemProps) {
  const isMobile = useIsMobile()
  const subType = isMobile ? "inline" : "popover" // no popover mode for mobile

  const [isOpen, setIsOpen] = useState(false)
  const isSelected =
    itemKey === selectedKey || selectedKey?.split("/").includes(itemKey)

  function handleMenuItemClick() {
    if (items.length > 0 && subType === "inline") setIsOpen(!isOpen)
    if (isFunction(onItemClick)) onItemClick({ key: itemKey, url, action })
  }

  const indentationClass = depth > 0 ? `ml-${depth * 2}` : ""

  const linkComponent = (
    <SideNavLink
      {...{
        itemKey,
        name,
        url,
        icon,
        action,
        isCollapsed,
        isPopoverItem,
        isSelected,
        render,
        onClick: handleMenuItemClick,
        hasItems: items.length > 0,
        isOpen,
        openInNewTab,
      }}
    />
  )

  if (items.length === 0) {
    return isCollapsed && depth === 0 ? (
      <div>
        <Tooltip key={itemKey}>
          <Tooltip.Trigger>{linkComponent}</Tooltip.Trigger>
          <Tooltip.Content>{name}</Tooltip.Content>
        </Tooltip>
      </div>
    ) : (
      <div className={indentationClass}>{linkComponent}</div>
    )
  }

  if (subType === "popover") {
    const options = items.map(
      ({ key, name, url, icon, render, action, items: subItems }) => {
        const display = (
          <SideNavItem
            key={key}
            {...{
              itemKey: key,
              name,
              url,
              icon,
              action,
              isCollapsed: false,
              isPopoverItem: true,
              selectedKey,
              render,
              items: subItems,
              subItemsType: subType,
              onItemClick,
              depth: 0,
              openInNewTab,
            }}
          />
        )
        return {
          key: key,
          display,
          selected: key === selectedKey,
        }
      }
    )

    const popoverOptions = options.reduce(
      (options, option) => ({
        ...options,
        [option.key]: option,
      }),
      {}
    )

    return (
      <PopoverMenuStyled
        options={popoverOptions}
        onSelect={(key) => {
          const menuItem = items.find((item) => item.key === key)
          if (menuItem?.action && isFunction(onItemClick)) onItemClick(menuItem)
        }}
        position="right-end"
        isClickType={false}
      >
        {linkComponent}
      </PopoverMenuStyled>
    )
  }

  return (
    <div className={indentationClass}>
      {linkComponent}
      {!isCollapsed && isOpen && (
        <div className="mt-1">
          {items.map(({ key, ...rest }) => (
            <SideNavItem
              {...rest}
              key={key}
              itemKey={key}
              isCollapsed={isCollapsed}
              subItemsType={rest.subItemsType || subItemsType}
              onItemClick={onItemClick}
              selectedKey={selectedKey}
              depth={depth + 1}
            />
          ))}
        </div>
      )}
    </div>
  )
}

export default function SideNav({
  isCollapsed,
  config,
  selectedKey,
  onItemClick,
}: SideNavProps) {
  return (
    <nav
      data-collapsed={isCollapsed}
      className={joinClassNames("group h-full", isCollapsed ? "items-center" : "")}
    >
      <ul
        className={joinClassNames("grid gap-1", isCollapsed ? "justify-center" : "")}
      >
        <li className="space-y-0.5">
          {config.map(({ key, ...rest }) => (
            <SideNavItem
              {...rest}
              itemKey={key}
              key={key}
              isCollapsed={isCollapsed}
              selectedKey={selectedKey}
              onItemClick={onItemClick}
            />
          ))}
        </li>
      </ul>
    </nav>
  )
}
